import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import DatePicker from "react-datepicker";
import { toast } from "react-hot-toast";
import CodeMirror from "@uiw/react-codemirror";
import { EditorView } from "@codemirror/view";
import { python } from "@codemirror/lang-python";
import { oneDark } from "@codemirror/theme-one-dark";
import ReferenceModal from "../components/ReferenceModal";
import UiBuilderModal from "../components/ViewModal";
import RelatedLists from "../components/RelatedLists";
import FilterBuilderComponent from "../components/FilterBuilderComponent";
import UiActionBar from "../components/UiActionBar";
import {
  ArrowLeft,
  Save,
  Trash2,
  Paperclip,
  Palette,
  LayoutGrid,
  ChevronRight,
  ChevronDown,
  Check,
  CopyPlus,
  MoreHorizontal,
  Sparkles,
  Search,
  ExternalLink,
  X,
  Code2,
  Maximize2,
  Minimize2,
  FileText,
  FileSpreadsheet,
  Image as ImageIcon,
  Archive,
  File as FileIcon,
  Loader2,
  CalendarDays,
  CalendarClock,
} from "lucide-react";

import "react-datepicker/dist/react-datepicker.css";
import "./FormView.css";

const AUTOSAVE_ENABLED = false;
const SYSTEM_CLONE_EXCLUDE_FIELDS = ["id", "created_at", "created_by", "updated_at", "updated_by"];
const authHeaders = (token) => ({ Authorization: `Bearer ${token}` });

const extractErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return fallback;
};

const isEnumField = (field) => {
  const type = String(field?.field_type || "").trim().toUpperCase();
  return type === "ENUM" || type === "CHOICE";
};
const isScriptField = (field) => String(field?.field_type || "").trim().toUpperCase() === "SCRIPT";
const isBooleanField = (field) => String(field?.field_type || "").toUpperCase().includes("BOOL");

const isNumberField = (field) => {
  const type = String(field?.field_type || "").toUpperCase();
  return type.includes("INT") || type.includes("DECIMAL") || type.includes("NUMERIC");
};

const isLongTextField = (field) => {
  const type = String(field?.field_type || "").toUpperCase();
  if (type.includes("TEXT") && (!field.length || field.length > 255)) return true;
  if (type.includes("VARCHAR") && field.length > 255) return true;
  return false;
};

const isFilterField = (field) => {
  const type = String(field?.field_type || "").toUpperCase();
  return type.includes("VARCHAR") && !!field.filter_table;
};

const parseDateForPicker = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (value === "null" || value === "undefined") return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const toDateOnlyString = (date) => {
  if (!date || Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getSafeNumberValue = (value) => {
  if (value === null || value === undefined || value === "" || Number.isNaN(Number(value))) return "";
  return Number(value);
};

const comparableValue = (value) => {
  if (value && typeof value === "object" && "id" in value) return value.id;
  return value;
};

const debounce = (fn, ms = 250) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), ms);
  };
};

const getIconForFile = (filename = "") => {
  const ext = filename.split(".").pop().toLowerCase();
  switch (ext) {
    case "pdf":
    case "doc":
    case "docx":
    case "txt":
      return FileText;
    case "xls":
    case "xlsx":
      return FileSpreadsheet;
    case "png":
    case "jpg":
    case "jpeg":
    case "gif":
      return ImageIcon;
    case "zip":
    case "rar":
      return Archive;
    default:
      return FileIcon;
  }
};

const formatPythonLikeCode = (code = "") => {
  const lines = String(code).replace(/\t/g, "    ").split("\n");
  let indent = 0;

  return lines
    .map((rawLine) => {
      const trimmed = rawLine.trim();
      if (!trimmed) return "";

      if (
        trimmed.startsWith("elif ") ||
        trimmed.startsWith("else:") ||
        trimmed.startsWith("except") ||
        trimmed.startsWith("finally:")
      ) {
        indent = Math.max(indent - 1, 0);
      }

      const formattedLine = `${"    ".repeat(indent)}${trimmed}`;
      if (trimmed.endsWith(":")) indent += 1;
      return formattedLine;
    })
    .join("\n")
    .trimEnd();
};

const ReferenceLookupInput = ({
  field,
  meta,
  value,
  onSelect,
  onClear,
  token,
  apiBaseUrl,
  disabled = false,
  onOpenModal,
}) => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const [text, setText] = useState(value?.display_value || "");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(0);

  useEffect(() => {
    setText(value?.display_value || "");
  }, [value?.id, value?.display_value]);

  const runSearch = useCallback(
    async (query) => {
      if (!field?.reference_table || !meta?.displayField) return;
      if (!query) {
        setItems([]);
        return;
      }

      setLoading(true);
      try {
        const filter = {
          logic: "AND",
          conditions: [{ field: meta.displayField, operator: "contains", value: query }],
        };

        const url = `${apiBaseUrl}/search/${field.reference_table}/?filter_json=${encodeURIComponent(
          JSON.stringify(filter)
        )}&limit=10`;

        const res = await axios.get(url, { headers: authHeaders(token) });
        const rows = Array.isArray(res.data?.records) ? res.data.records : [];

        setItems(
          rows.map((row) => ({
            id: row.id,
            display_value: row[meta.displayField] ?? row.display_value ?? row.id,
          }))
        );
        setActive(0);
      } catch (error) {
        console.error("Reference lookup search failed:", error);
        setItems([]);
      } finally {
        setLoading(false);
      }
    },
    [apiBaseUrl, field?.reference_table, meta?.displayField, token]
  );

  const debouncedSearch = useMemo(() => debounce(runSearch, 200), [runSearch]);

  const handleChange = (event) => {
    if (disabled) return;
    const query = event.target.value;
    setText(query);
    setOpen(true);
    debouncedSearch(query);
  };

  const handleSelect = (option) => {
    onSelect(option);
    setOpen(false);
    setText(option.display_value || "");
  };

  const handleKeyDown = (event) => {
    if (!open) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive((current) => Math.min(current + 1, items.length - 1));
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((current) => Math.max(current - 1, 0));
    }

    if (event.key === "Enter") {
      event.preventDefault();
      if (items[active]) handleSelect(items[active]);
    }

    if (event.key === "Escape") setOpen(false);
  };

  useEffect(() => {
    const closeIfOutside = (event) => {
      if (listRef.current?.contains(event.target) || inputRef.current?.contains(event.target)) return;
      setOpen(false);
    };

    document.addEventListener("mousedown", closeIfOutside);
    return () => document.removeEventListener("mousedown", closeIfOutside);
  }, []);

  return (
    <div className="fv-reference-lookup">
      <input
        ref={inputRef}
        type="text"
        value={text}
        onChange={handleChange}
        onFocus={() => {
          if (!disabled) {
            setOpen(true);
            debouncedSearch(text || "");
          }
        }}
        onKeyDown={handleKeyDown}
        placeholder={`Search ${field.label || field.name}...`}
        disabled={disabled}
        className="fv-input"
      />

      {value?.id && !disabled && (
        <button
          type="button"
          onClick={() => navigate(`/dashboard/${field.reference_table}/form_view/${value.id}`)}
          title="Open record"
          className="sn-btn sn-btn--ghost fv-icon-button"
        >
          <ExternalLink size={15} />
        </button>
      )}

      {!disabled && (
        <button
          type="button"
          onClick={onOpenModal}
          title={`Search ${field.label || field.name}...`}
          className="sn-btn sn-btn--ghost fv-icon-button"
        >
          <Search size={15} />
        </button>
      )}

      {value?.id && !disabled && (
        <button
          type="button"
          onClick={() => {
            onClear();
            setText("");
          }}
          title="Clear"
          className="sn-btn sn-btn--ghost fv-icon-button"
        >
          <X size={15} />
        </button>
      )}

      {open && (
        <div ref={listRef} className="fv-reference-suggestions">
          {loading ? (
            <div className="fv-reference-empty">Searching...</div>
          ) : items.length === 0 ? (
            <div className="fv-reference-empty">No matches</div>
          ) : (
            items.map((option, index) => (
              <button
                key={option.id}
                type="button"
                onMouseDown={(event) => {
                  event.preventDefault();
                  handleSelect(option);
                }}
                className={`fv-reference-option ${index === active ? "is-active" : ""}`}
              >
                {option.display_value}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const FormView = () => {
  const { tableName, recordId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const token = localStorage.getItem("token");
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const refField = queryParams.get("refField");
  const refValue = queryParams.get("refValue");
  const refDisplay = queryParams.get("refDisplay");

  const [bootstrapData, setBootstrapData] = useState(null);
  const [fullscreenScriptField, setFullscreenScriptField] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showAttachments, setShowAttachments] = useState(false);
  const [tableId, setTableId] = useState(null);
  const [tableLabel, setTableLabel] = useState("");
  const [tables, setTables] = useState([]);
  const [fields, setFields] = useState([]);
  const [originalRecord, setOriginalRecord] = useState({});
  const [record, setRecord] = useState({});
  const [referencePopup, setReferencePopup] = useState(null);
  const [referenceFields, setReferenceFields] = useState([]);
  const [refMeta, setRefMeta] = useState({});
  const [isUiBuilderOpen, setUiBuilderOpen] = useState(false);
  const [activeViewColumns, setActiveViewColumns] = useState(null);
  const [availableViews, setAvailableViews] = useState([]);
  const [selectedViewId, setSelectedViewId] = useState(null);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [isViewsOpen, setIsViewsOpen] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isInserting, setIsInserting] = useState(false);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState(null);

  const autosaveRef = useRef(null);
  const loadFormAbortRef = useRef(null);
  const referenceFieldsCacheRef = useRef({});

  const buildViewColumnMap = useCallback((viewId, allColumns) => {
    const map = {};
    (allColumns || [])
      .filter((column) => column.sip_view_id === viewId)
      .forEach((column) => {
        map[column.sip_field_name] = {
          position: column.sip_position || "center",
          order: column.sip_column_order || 0,
        };
      });
    return map;
  }, []);

  const buildFieldsWithChoices = useCallback((bootstrap) => {
    return (bootstrap.fields || []).map((field) => {
      const choices = bootstrap.choices_by_field_id?.[field.id] || [];
      const options = choices
        .map((choice) => ({
          value: choice.sip_value,
          label: choice.sip_label || choice.sip_value,
          order: Number(choice.sip_order || 0),
          is_active: choice.sip_is_active !== false,
        }))
        .filter((choice) => choice.is_active)
        .sort((a, b) => a.order - b.order);

      return {
        ...field,
        allowed_options: options,
        allowed_values: options.map((option) => option.value),
      };
    });
  }, []);

  const fetchAttachments = useCallback(async () => {
    if (!recordId || recordId === "new") {
      setAttachments([]);
      return;
    }

    try {
      const res = await axios.get(`${apiBaseUrl}/attachments_get/${recordId}`, {
        headers: authHeaders(token),
      });
      setAttachments(res.data || []);
    } catch (error) {
      console.error("Failed to fetch attachments:", error);
    }
  }, [apiBaseUrl, recordId, token]);

  const buildNewRecord = useCallback(
    async (fieldList) => {
      const nextRecord = {};

      fieldList.forEach((field) => {
        if (SYSTEM_CLONE_EXCLUDE_FIELDS.includes(field.name)) return;

        if (isBooleanField(field)) {
          nextRecord[field.name] = field.default_value === true || field.default_value === "true";
        } else if (field.default_value !== undefined && field.default_value !== null && field.default_value !== "") {
          nextRecord[field.name] = field.default_value;
        } else {
          nextRecord[field.name] = "";
        }
      });

      if (refField && refValue) {
        const refFieldDef = fieldList.find((field) => field.name === refField);

        if (refFieldDef?.reference_table) {
          if (refDisplay) {
            nextRecord[refField] = {
              id: refValue,
              display_value: decodeURIComponent(refDisplay),
            };
          } else {
            try {
              const res = await axios.get(`${apiBaseUrl}/${refFieldDef.reference_table}/${refValue}`, {
                headers: authHeaders(token),
              });

              nextRecord[refField] = {
                id: refValue,
                display_value: res.data?.display_value || refValue,
              };
            } catch (error) {
              console.error("Failed to fetch reference value:", error);
              nextRecord[refField] = { id: refValue, display_value: refValue };
            }
          }
        }
      }

      return nextRecord;
    },
    [apiBaseUrl, refDisplay, refField, refValue, token]
  );

  const loadForm = useCallback(async () => {
    if (!tableName || !recordId || !token) return;

    loadFormAbortRef.current?.abort();
    const controller = new AbortController();
    loadFormAbortRef.current = controller;

    setIsLoading(true);
    setBootstrapData(null);
    setRecord({});
    setOriginalRecord({});
    setFields([]);
    setTableId(null);
    setTableLabel("");
    setActiveViewColumns(null);
    setAvailableViews([]);
    setSelectedViewId(null);
    setRefMeta({});
    setAttachments([]);

    try {
      const bootstrapRes = await axios.get(`${apiBaseUrl}/form_bootstrap/${tableName}/${recordId}`, {
        headers: authHeaders(token),
        signal: controller.signal,
      });

      const bootstrap = bootstrapRes.data || {};
      setBootstrapData(bootstrap);

      setTables(bootstrap.tables || [bootstrap.table].filter(Boolean));
      setTableId(bootstrap.table?.id || null);
      setTableLabel(bootstrap.table?.label || tableName);

      const fieldList = buildFieldsWithChoices(bootstrap);
      setFields(fieldList);

      const referenceMeta = {};
      fieldList
        .filter((field) => field.reference_table)
        .forEach((field) => {
          referenceMeta[field.name] = { displayField: "id" };
        });
      setRefMeta(referenceMeta);

      const formViews = (bootstrap.views || []).filter((view) => view.sip_view_type === "form");
      setAvailableViews(formViews);

      const defaultView = formViews.find((view) => view.sip_is_default) || formViews[0];

      if (defaultView) {
        setSelectedViewId(defaultView.id);
        setActiveViewColumns(buildViewColumnMap(defaultView.id, bootstrap.view_columns || []));
      }

      if (recordId.toLowerCase().trim() === "new") {
        const newRecord = await buildNewRecord(fieldList);
        setRecord(newRecord);
        setOriginalRecord(newRecord);
      } else {
        setRecord(bootstrap.record || {});
        setOriginalRecord(bootstrap.record || {});
        setAttachments(bootstrap.attachments || []);
      }
    } catch (error) {
      if (axios.isCancel(error) || controller.signal.aborted) return;
      console.error("Error loading form:", error);
      toast.error(extractErrorMessage(error, "Failed to load record."));
    } finally {
      if (loadFormAbortRef.current === controller) {
        setIsLoading(false);
      }
    }
  }, [
    apiBaseUrl,
    buildFieldsWithChoices,
    buildNewRecord,
    buildViewColumnMap,
    recordId,
    tableName,
    token,
  ]);

  useEffect(() => {
    loadForm();
    return () => loadFormAbortRef.current?.abort();
  }, [loadForm]);

  useEffect(() => {
    if (!originalRecord || !Object.keys(originalRecord).length) return;
    const changed = Object.keys(record || {}).some(
      (key) => comparableValue(record[key]) !== comparableValue(originalRecord[key])
    );
    setIsDirty(changed);
  }, [record, originalRecord]);

  useEffect(() => {
    const handler = (event) => {
      if (!isDirty) return;
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const handleChange = useCallback((event, field) => {
    let value;

    if (isBooleanField(field)) value = event.target.checked;
    else if (isNumberField(field)) value = event.target.value === "" ? null : Number(event.target.value);
    else value = event.target.value;

    setRecord((prev) => ({ ...prev, [field.name]: value }));
  }, []);

  const validateMandatoryFields = useCallback(() => {
    const missingFields = fields
      .filter(
        (field) =>
          field.is_mandatory &&
          (record[field.name] === "" || record[field.name] === null || record[field.name] === undefined)
      )
      .map((field) => field.label || field.name);

    if (missingFields.length > 0) {
      toast.error(`Mandatory fields missing: ${missingFields.join(", ")}`);
      return false;
    }

    return true;
  }, [fields, record]);

  const buildModifiedFields = useCallback(() => {
    const isNew = recordId === "new";

    return Object.keys(record).reduce((acc, key) => {
      if (SYSTEM_CLONE_EXCLUDE_FIELDS.includes(key)) return acc;

      const field = fields.find((item) => item.name === key);
      let newValue = record[key];
      const originalValue = originalRecord[key];

      if (newValue && typeof newValue === "object" && "id" in newValue) {
        newValue = newValue.id;
      }

      if (field?.filter_table && typeof newValue === "object") {
        newValue = JSON.stringify(newValue);
      }

      if (newValue === "" && !isBooleanField(field)) newValue = null;
      if (isBooleanField(field)) newValue = !!newValue;

      if (
        String(field?.field_type || "").toUpperCase().includes("TIMESTAMP") &&
        typeof newValue === "string" &&
        newValue
      ) {
        try {
          const date = new Date(newValue);
          newValue = Number.isNaN(date.getTime()) ? null : date.toISOString();
        } catch (error) {
          console.error(`Failed to convert datetime for field '${field.name}':`, newValue, error);
        }
      }

      if (isNew) {
        if (newValue !== null && newValue !== undefined && newValue !== "") {
          acc[key] = newValue;
        }
        return acc;
      }

      if (comparableValue(newValue) !== comparableValue(originalValue)) acc[key] = newValue;
      return acc;
    }, {});
  }, [fields, originalRecord, record, recordId]);

  const handleSave = useCallback(
    async (redirect = true) => {
      if (isSaving) return;
      if (!validateMandatoryFields()) return;

      const modifiedFields = buildModifiedFields();

      if (Object.keys(modifiedFields).length === 0) {
        toast("No changes detected.");
        return;
      }

      setIsSaving(true);
      try {
        const isNew = recordId === "new";
        const requestUrl = isNew ? `${apiBaseUrl}/${tableName}/` : `${apiBaseUrl}/${tableName}/${recordId}`;

        const response = isNew
          ? await axios.post(requestUrl, modifiedFields, { headers: authHeaders(token) })
          : await axios.put(requestUrl, modifiedFields, { headers: authHeaders(token) });

        const newId = response.data?.record?.id;
        toast.success("Saved");

        if (redirect) {
          navigate(`/dashboard/${tableName}/list_view`);
          return;
        }

        if (isNew && newId) {
          navigate(`/dashboard/${tableName}/form_view/${newId}`, { replace: true });
          return;
        }

        setOriginalRecord({ ...record });
        setIsDirty(false);

        if (!isNew) {
          await loadForm();
        }
      } catch (error) {
        console.error("Failed to save record:", error);
        toast.error(extractErrorMessage(error, "Failed to save record."));
      } finally {
        setIsSaving(false);
      }
    },
    [
      apiBaseUrl,
      buildModifiedFields,
      isSaving,
      loadForm,
      navigate,
      record,
      recordId,
      tableName,
      token,
      validateMandatoryFields,
    ]
  );

  useEffect(() => {
    const onKey = (event) => {
      const isSave = (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "s";
      if (isSave) {
        event.preventDefault();
        handleSave(true);
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleSave]);

  useEffect(() => {
    if (!AUTOSAVE_ENABLED || !isDirty) return undefined;
    clearTimeout(autosaveRef.current);
    autosaveRef.current = setTimeout(() => handleSave(false), 1500);
    return () => clearTimeout(autosaveRef.current);
  }, [handleSave, isDirty, record]);

  useEffect(() => {
    if (!isMoreOpen) return undefined;

    const handleClickOutside = (event) => {
      const moreButton = document.getElementById("more-button");
      const dropdownContent = document.getElementById("more-dropdown");

      if (moreButton && !moreButton.contains(event.target) && dropdownContent && !dropdownContent.contains(event.target)) {
        setIsMoreOpen(false);
        setIsViewsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMoreOpen]);

  const openReferencePopup = useCallback(
    async (field) => {
      if (!field.reference_table) return;

      setReferencePopup(null);

      const cached = referenceFieldsCacheRef.current[field.reference_table];
      if (cached) {
        setReferenceFields(cached.refFields);
        setReferencePopup({
          ...field,
          referenceTable: field.reference_table,
          displayField: cached.displayField,
        });
        return;
      }

      try {
        const fieldRes = await axios.get(`${apiBaseUrl}/search/table_fields/?table_id=${field.reference_table}`, {
          headers: authHeaders(token),
        });

        const refFields = fieldRes.data?.records || [];
        const displayField = refFields.find((item) => item.display === true)?.name || "id";

        referenceFieldsCacheRef.current[field.reference_table] = { refFields, displayField };

        setReferenceFields(refFields);
        setReferencePopup({
          ...field,
          referenceTable: field.reference_table,
          displayField,
        });
      } catch (error) {
        console.error("Failed to open reference modal:", error);
        toast.error(extractErrorMessage(error, "Failed to open reference lookup."));
      }
    },
    [apiBaseUrl, token]
  );

  const selectReferenceRecord = useCallback(
    (selectedRecord) => {
      if (!referencePopup) return;

      const displayField = referencePopup.displayField || "id";

      setRecord((prev) => ({
        ...prev,
        [referencePopup.name]: {
          id: selectedRecord.id,
          display_value: selectedRecord[displayField] || selectedRecord.display_value || selectedRecord.id,
        },
      }));

      setReferencePopup(null);
    },
    [referencePopup]
  );

  const handleDelete = useCallback(async () => {
    if (isDeleting) return;

    if (!recordId || recordId === "new") {
      toast.error("You cannot delete a new/unsaved record.");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this record?")) return;

    setIsDeleting(true);
    try {
      await axios.delete(`${apiBaseUrl}/${tableName}/${recordId}`, { headers: authHeaders(token) });
      toast.success("Record deleted successfully.");
      navigate(`/dashboard/${tableName}/list_view`);
    } catch (error) {
      console.error("Failed to delete record:", error);
      toast.error(extractErrorMessage(error, "Failed to delete record."));
      setIsDeleting(false);
    }
  }, [apiBaseUrl, isDeleting, navigate, recordId, tableName, token]);

  const handleInsertAndStay = useCallback(async () => {
    if (isInserting) return;

    setIsInserting(true);
    try {
      const dataToClone = {};

      fields.forEach((field) => {
        if (!SYSTEM_CLONE_EXCLUDE_FIELDS.includes(field.name)) {
          const value = record[field.name];
          dataToClone[field.name] = value && typeof value === "object" && "id" in value ? value.id : value;
        }
      });

      const res = await axios.post(`${apiBaseUrl}/${tableName}/`, dataToClone, {
        headers: authHeaders(token),
      });

      const newId = res.data?.record?.id;
      if (newId) {
        toast.success("Record duplicated.");
        navigate(`/dashboard/${tableName}/form_view/${newId}`, { replace: true });
      }
    } catch (error) {
      console.error("Failed to duplicate record:", error);
      toast.error(extractErrorMessage(error, "Failed to duplicate record."));
    } finally {
      setIsInserting(false);
    }
  }, [apiBaseUrl, fields, isInserting, navigate, record, tableName, token]);

  const handleDeleteAttachment = useCallback(
    async (attachmentId) => {
      if (deletingAttachmentId) return;
      if (!window.confirm("Are you sure you want to delete this attachment?")) return;

      setDeletingAttachmentId(attachmentId);
      try {
        await axios.delete(`${apiBaseUrl}/attachments/${attachmentId}/`, {
          headers: authHeaders(token),
        });
        await fetchAttachments();
        toast.success("Attachment deleted successfully.");
      } catch (error) {
        console.error("Failed to delete attachment:", error);
        toast.error(extractErrorMessage(error, "Failed to delete attachment."));
      } finally {
        setDeletingAttachmentId(null);
      }
    },
    [apiBaseUrl, deletingAttachmentId, fetchAttachments, token]
  );

  const downloadAttachment = useCallback(
    async (attachmentId, filename) => {
      try {
        const res = await fetch(`${apiBaseUrl}/attachments-download/${attachmentId}`, {
          headers: authHeaders(token),
        });

        if (!res.ok) throw new Error("Download failed");

        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = filename;
        link.click();

        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Download failed:", error);
        toast.error("Failed to download attachment.");
      }
    },
    [apiBaseUrl, token]
  );

  const uploadAttachment = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      if (!file || !tableId || recordId === "new" || isUploadingAttachment) return;

      const formData = new FormData();
      formData.append("table_id", tableId);
      formData.append("record_id", recordId);
      formData.append("file", file);

      setIsUploadingAttachment(true);
      try {
        await axios.post(`${apiBaseUrl}/attachments/upload/`, formData, {
          headers: {
            ...authHeaders(token),
            "Content-Type": "multipart/form-data",
          },
        });

        toast.success("File uploaded successfully.");
        await fetchAttachments();
        event.target.value = "";
      } catch (error) {
        console.error("Failed to upload attachment:", error);
        toast.error(extractErrorMessage(error, "Failed to upload attachment."));
      } finally {
        setIsUploadingAttachment(false);
      }
    },
    [apiBaseUrl, fetchAttachments, isUploadingAttachment, recordId, tableId, token]
  );

  const structuredFieldRows = useMemo(() => {
    if (!fields.length) return [];

    const visibleFields = activeViewColumns
      ? fields
          .filter((field) => activeViewColumns[field.name])
          .map((field) => ({
            ...field,
            position: activeViewColumns[field.name]?.position || "center",
            order: activeViewColumns[field.name]?.order || 0,
          }))
          .sort((a, b) => a.order - b.order)
      : fields.filter((field) => field.name !== "id").map((field) => ({ ...field, position: "center", order: 0 }));

    const leftQueue = [];
    const rightQueue = [];
    const rows = [];

    for (const field of visibleFields) {
      if (field.position === "center") {
        while (leftQueue.length || rightQueue.length) {
          const left = leftQueue.shift();
          const right = rightQueue.shift();
          const row = [left, right].filter(Boolean);
          if (row.length) rows.push(row);
        }
        rows.push([field]);
      } else if (field.position === "left") {
        leftQueue.push(field);
      } else if (field.position === "right") {
        rightQueue.push(field);
      }
    }

    while (leftQueue.length || rightQueue.length) {
      const left = leftQueue.shift();
      const right = rightQueue.shift();
      const row = [left, right].filter(Boolean);
      if (row.length) rows.push(row);
    }

    return rows;
  }, [activeViewColumns, fields]);

  const renderField = useCallback(
    (field) => {
      const value = record[field.name];
      const fieldType = String(field.field_type || "").toUpperCase();
      const disabled = !!field.is_read_only || field.acl_writable === false;
      const invalid = field.is_mandatory && (value === "" || value === null || value === undefined);
      const fieldClassName = `fv-field ${invalid ? "is-invalid" : ""} ${disabled ? "is-readonly" : ""}`;

      let control;

      if (isBooleanField(field)) {
        control = (
          <div className="fv-checkbox-wrapper">
            <input
              type="checkbox"
              checked={!!value}
              onChange={(event) => handleChange(event, field)}
              disabled={disabled}
              className="fv-checkbox"
            />
          </div>
        );
      } else if (isFilterField(field)) {
        control = (
          <FilterBuilderComponent
            filterTable={field.filter_table}
            filterValue={value !== undefined && value !== null ? value : ""}
            onChange={(nextValue) => setRecord((prev) => ({ ...prev, [field.name]: nextValue }))}
            token={token}
            apiBaseUrl={apiBaseUrl}
            isEmbeddedInListView={false}
          />
        );
      } else if (isEnumField(field)) {
        control = field.allowed_options?.length ? (
          <select
            value={String(value ?? "")}
            onChange={(event) => handleChange(event, field)}
            disabled={disabled}
            className="fv-select"
          >
            <option value="">— Select —</option>
            {field.allowed_options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label || option.value}
              </option>
            ))}
          </select>
        ) : (
          <div className="fv-muted">No choices available.</div>
        );
      } else if (fieldType.includes("TIMESTAMP")) {
        control = (
          <div className="fv-date-row">
            <DatePicker
              selected={parseDateForPicker(value)}
              onChange={(date) => {
                setRecord((prev) => ({
                  ...prev,
                  [field.name]: date && !Number.isNaN(date.getTime()) ? date.toISOString() : "",
                }));
              }}
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              dateFormat="yyyy-MM-dd HH:mm"
              placeholderText="Select date & time"
              disabled={disabled}
              customInput={<input className="fv-input" />}
              portalId="sip-datepicker-portal"
              popperPlacement="bottom-start"
            />

            {!disabled && (
              <>
                <button
                  type="button"
                  className="sn-btn sn-btn--ghost"
                  onClick={() => setRecord((prev) => ({ ...prev, [field.name]: new Date().toISOString() }))}
                >
                  <CalendarClock size={14} /> Now
                </button>
                <button
                  type="button"
                  className="sn-btn sn-btn--ghost"
                  onClick={() => setRecord((prev) => ({ ...prev, [field.name]: "" }))}
                >
                  <X size={14} /> Clear
                </button>
              </>
            )}
          </div>
        );
      } else if (fieldType.includes("DATE")) {
        control = (
          <div className="fv-date-row">
            <DatePicker
              selected={parseDateForPicker(value)}
              onChange={(date) => {
                setRecord((prev) => ({
                  ...prev,
                  [field.name]: date && !Number.isNaN(date.getTime()) ? toDateOnlyString(date) : "",
                }));
              }}
              dateFormat="yyyy-MM-dd"
              placeholderText="Select a date"
              disabled={disabled}
              shouldCloseOnSelect
              customInput={<input className="fv-input" />}
              portalId="sip-datepicker-portal"
              popperPlacement="bottom-start"
            />

            {!disabled && (
              <>
                <button
                  type="button"
                  className="sn-btn sn-btn--ghost"
                  onClick={() => setRecord((prev) => ({ ...prev, [field.name]: toDateOnlyString(new Date()) }))}
                >
                  <CalendarDays size={14} /> Today
                </button>
                <button
                  type="button"
                  className="sn-btn sn-btn--ghost"
                  onClick={() => setRecord((prev) => ({ ...prev, [field.name]: "" }))}
                >
                  <X size={14} /> Clear
                </button>
              </>
            )}
          </div>
        );
      } else if (field.reference_table) {
        control = (
          <ReferenceLookupInput
            field={field}
            meta={refMeta[field.name] || { displayField: "id" }}
            value={value || null}
            token={token}
            apiBaseUrl={apiBaseUrl}
            disabled={disabled}
            onSelect={(option) => {
              setRecord((prev) => ({
                ...prev,
                [field.name]: { id: option.id, display_value: option.display_value },
              }));
            }}
            onClear={() => setRecord((prev) => ({ ...prev, [field.name]: null }))}
            onOpenModal={() => openReferencePopup(field)}
          />
        );
      } else if (isScriptField(field)) {
        const isFullscreen = fullscreenScriptField === field.name;

        control = (
          <div className={isFullscreen ? "fv-script-fullscreen" : "fv-script-editor"}>
            <div className="fv-script-toolbar">
              <span className="fv-script-toolbar-label">
                <Code2 size={14} /> Python Script
              </span>

              <div className="fv-script-actions">
                {!disabled && (
                  <button
                    type="button"
                    className="sn-btn sn-btn--ghost fv-script-format-btn"
                    onClick={() => {
                      setRecord((prev) => ({
                        ...prev,
                        [field.name]: formatPythonLikeCode(prev[field.name] || ""),
                      }));
                    }}
                  >
                    Format
                  </button>
                )}

                <button
                  type="button"
                  className="sn-btn sn-btn--ghost fv-script-format-btn"
                  onClick={() => setFullscreenScriptField(isFullscreen ? null : field.name)}
                >
                  {isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                  {isFullscreen ? "Exit Full Screen" : "Full Screen"}
                </button>
              </div>
            </div>

            <CodeMirror
              value={value || ""}
              height={isFullscreen ? "calc(100vh - 72px)" : "360px"}
              extensions={[python(), EditorView.lineWrapping]}
              theme={oneDark}
              editable={!disabled}
              basicSetup={{
                lineNumbers: true,
                foldGutter: true,
                bracketMatching: true,
                autocompletion: true,
                highlightActiveLine: true,
                indentOnInput: true,
              }}
              onChange={(nextValue) => {
                setRecord((prev) => ({
                  ...prev,
                  [field.name]: nextValue,
                }));
              }}
            />
          </div>
        );
      } else if (isLongTextField(field)) {
        control = (
          <textarea
            value={value ?? ""}
            onChange={(event) => handleChange(event, field)}
            disabled={disabled}
            className="fv-textarea"
          />
        );
      } else {
        control = (
          <input
            type={isNumberField(field) ? "number" : "text"}
            value={isNumberField(field) ? getSafeNumberValue(value) : value ?? ""}
            onChange={(event) => handleChange(event, field)}
            disabled={disabled}
            className="fv-input"
          />
        );
      }

      return (
        <div key={field.name} className={fieldClassName}>
          <label className="fv-label">
            {field.label || field.name} {field.is_mandatory && <span className="fv-required">*</span>}
          </label>
          {control}
        </div>
      );
    },
    [
      apiBaseUrl,
      fullscreenScriptField,
      handleChange,
      openReferencePopup,
      record,
      refMeta,
      token,
    ]
  );

  if (isLoading) {
    return (
      <div className="fv-loading fv-loading-premium">
        <div className="fv-loading-card">
          <div className="fv-loading-icon">
            <Sparkles size={34} />
          </div>
          <h2>Preparing your workspace</h2>
          <p>Loading record, fields, views and related data…</p>

          <div className="fv-skeleton-header">
            <div className="fv-skeleton fv-skeleton-btn" />
            <div className="fv-skeleton fv-skeleton-btn" />
            <div className="fv-skeleton fv-skeleton-btn" />
          </div>

          <div className="fv-skeleton-rows">
            {[100, 100, 70, 100, 55].map((width, index) => (
              <div key={index} className="fv-skeleton fv-skeleton-row" style={{ width: `${width}%` }} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fv-container fv-animated-page">
      <div className="fv-sticky-header">
        <div className="fv-smart-header">
          <div className="fv-header-left">
            <button className="sn-btn sn-btn--neutral" onClick={() => navigate(`/dashboard/${tableName}/list_view`)}>
              <ArrowLeft size={16} /> Back to List
            </button>

            <button
              className="sn-btn sn-btn--primary"
              onClick={() => handleSave(true)}
              disabled={isSaving}
              aria-busy={isSaving}
            >
              {isSaving ? <Loader2 size={16} className="fv-spin" /> : <Save size={16} />}
              {isSaving ? "Saving…" : "Save"}
            </button>

            <UiActionBar
              tableName={tableName}
              recordId={recordId}
              record={record}
              setRecord={setRecord}
              token={token}
              apiBaseUrl={apiBaseUrl}
              navigate={navigate}
              handleSave={handleSave}
              loadForm={loadForm}
              initialActions={bootstrapData?.ui_actions || []}
            />

            {recordId !== "new" && (
              <button
                className="sn-btn sn-btn--danger"
                onClick={handleDelete}
                disabled={isDeleting}
                aria-busy={isDeleting}
              >
                {isDeleting ? <Loader2 size={16} className="fv-spin" /> : <Trash2 size={16} />}
                {isDeleting ? "Deleting…" : "Delete"}
              </button>
            )}

            <div className="fv-dropdown-wrapper">
              <button
                id="more-button"
                className="sn-btn sn-btn--ghost fv-more-button"
                onClick={() => setIsMoreOpen((prev) => !prev)}
              >
                <MoreHorizontal size={16} /> More <ChevronDown size={14} />
              </button>

              {isMoreOpen && (
                <div id="more-dropdown" className="sn-menu fv-dropdown-content">
                  <button
                    className="sn-menu__item"
                    disabled={isSaving}
                    onClick={() => {
                      handleSave(false);
                      setIsMoreOpen(false);
                    }}
                  >
                    <Save size={14} /> {isSaving ? "Saving…" : "Save & Stay"}
                  </button>

                  <button
                    className="sn-menu__item"
                    disabled={isInserting}
                    onClick={() => {
                      handleInsertAndStay();
                      setIsMoreOpen(false);
                    }}
                  >
                    <CopyPlus size={14} /> {isInserting ? "Duplicating…" : "Insert & Stay"}
                  </button>

                  <button
                    className="sn-menu__item"
                    onClick={() => {
                      setUiBuilderOpen(true);
                      setIsMoreOpen(false);
                    }}
                  >
                    <Palette size={14} /> UI Builder
                  </button>

                  <div className="fv-nested-menu-wrapper">
                    <button className="sn-menu__item fv-menu-with-arrow" onClick={() => setIsViewsOpen((prev) => !prev)}>
                      <span className="fv-menu-item-label">
                        <LayoutGrid size={14} /> Views
                      </span>
                      <ChevronRight size={14} />
                    </button>

                    {isViewsOpen && (
                      <div className="fv-nested-dropdown">
                        {availableViews.length > 0 ? (
                          availableViews.map((view) => (
                            <button
                              key={view.id}
                              className={`sn-menu__item ${view.id === selectedViewId ? "is-selected" : ""}`}
                              onClick={() => {
                                setSelectedViewId(view.id);
                                setActiveViewColumns(buildViewColumnMap(view.id, bootstrapData?.view_columns || []));
                                setIsMoreOpen(false);
                                setIsViewsOpen(false);
                              }}
                            >
                              <span className="fv-menu-item-label">{view.sip_name || `View ${view.id}`}</span>
                              {view.id === selectedViewId && <Check size={14} />}
                            </button>
                          ))
                        ) : (
                          <div className="fv-menu-empty">No Views Available</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="fv-header-right">
            {recordId !== "new" && tableId && (
              <div className="fv-inline-attachment">
                <label
                  htmlFor="attachment-upload"
                  className={`fv-upload-label${isUploadingAttachment ? " fv-upload-label--busy" : ""}`}
                  title={isUploadingAttachment ? "Uploading…" : "Upload attachment"}
                  aria-busy={isUploadingAttachment}
                >
                  {isUploadingAttachment ? <Loader2 size={18} className="fv-spin" /> : <Paperclip size={18} />}
                </label>
                <input
                  type="file"
                  id="attachment-upload"
                  className="fv-hidden-input"
                  onChange={uploadAttachment}
                  disabled={isUploadingAttachment}
                />

                {attachments.length > 0 && (
                  <div className="fv-attachment-dropdown">
                    <button className="fv-attachment-toggle" onClick={() => setShowAttachments((prev) => !prev)}>
                      {showAttachments ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      Attachments {`(${attachments.length})`}
                    </button>

                    {showAttachments && (
                      <div className="fv-attachment-list">
                        {attachments.map((attachment) => {
                          const FileTypeIcon = getIconForFile(attachment.sip_filename);
                          return (
                            <div key={attachment.id} className="fv-attachment-card">
                              <span className="fv-attachment-icon">
                                <FileTypeIcon size={16} />
                              </span>

                              <button
                                type="button"
                                onClick={() => downloadAttachment(attachment.id, attachment.sip_filename)}
                                className="fv-attachment-link"
                                title="Click to download"
                              >
                                {attachment.sip_filename || "Unnamed file"}
                              </button>

                              <span className="fv-attachment-size">
                                {attachment.sip_file_size ? `${(attachment.sip_file_size / 1024).toFixed(1)} KB` : ""}
                              </span>

                              <button
                                type="button"
                                onClick={() => handleDeleteAttachment(attachment.id)}
                                title="Delete attachment"
                                className="fv-attachment-delete"
                                disabled={deletingAttachmentId === attachment.id}
                                aria-busy={deletingAttachmentId === attachment.id}
                              >
                                {deletingAttachmentId === attachment.id ? (
                                  <Loader2 size={15} className="fv-spin" />
                                ) : (
                                  <Trash2 size={15} />
                                )}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="lv-chip fv-status-chip">
              <span className={`status-dot ${isDirty ? "is-dirty" : "is-clean"}`} />
              {isDirty ? "Unsaved changes" : "All changes saved"}
            </div>
          </div>
        </div>
      </div>

      <div className="fv-title-bar">
        <h2 className="fv-table-title">
          <FileText size={18} className="fv-table-title-icon" />
          {tableLabel}
          {isDirty ? " •" : ""}
        </h2>
      </div>

      <div className="fv-dynamic-form">
        {structuredFieldRows.map((row, index) => (
          <div
            key={index}
            className="fv-row fv-animated-row"
            style={{ animationDelay: `${Math.min(index * 0.035, 0.35)}s` }}
          >
            {row.map((field) => (
              <div key={field.name} className={field.position === "center" ? "fv-center-field" : "fv-side-field"}>
                {renderField(field)}
              </div>
            ))}
          </div>
        ))}

        {recordId !== "new" && <RelatedLists tableName={tableName} recordId={recordId} />}
      </div>

      <ReferenceModal
        isOpen={!!referencePopup}
        title={`Select ${referencePopup?.label || "record"}`}
        referenceTable={referencePopup?.referenceTable}
        fields={referenceFields}
        displayField={referencePopup?.displayField}
        token={token}
        apiBaseUrl={apiBaseUrl}
        onSelect={selectReferenceRecord}
        onClose={() => setReferencePopup(null)}
        tableFieldId={referencePopup?.id}
        currentRecord={record}
      />

      <UiBuilderModal
        isOpen={isUiBuilderOpen}
        onClose={() => setUiBuilderOpen(false)}
        onSaved={() => {
          setUiBuilderOpen(false);
          loadForm();
        }}
        tableId={tableId}
        tableName={tableName}
      />
    </div>
  );
};

export default FormView;